import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import type { AppState } from '@auth0/auth0-react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';

import { resetMixpanelUser } from 'configs/mixpanelConfig';
import LoadingSpinner from 'components/data/LoadingSpinner/LoadingSpinner';
import ErrorAlert from 'components/error/ErrorAlert/ErrorAlert';

const { VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID } = import.meta.env;

export function useAuthContext() {
  const auth0 = useAuth0();

  return {
    ...auth0,
    logout: async () => {
      resetMixpanelUser();

      // These options ensure that we are always redirected to the same environment
      // we logged out from. If we didn't pass in these options, we would be redirected
      // to the production site even if we logged out from the develop site.
      await auth0.logout({
        logoutParams: { returnTo: import.meta.env.VITE_HOST },
      });
    },
  };
}

export default function AuthProvider({ children }: { children: JSX.Element }) {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  if (!VITE_AUTH0_DOMAIN || !VITE_AUTH0_CLIENT_ID) {
    return (
      <ErrorAlert
        isFullScreen
        title="Unable to determine authorization environment"
        icon={<FaExclamationTriangle />}
        message="Unable to determine authorization environment. Please contact us. In the meantime, try refreshing the page."
      />
    );
  }

  const onRedirectCallback = (appState?: AppState) => {
    if (!appState) {
      navigate(window.location.pathname);
      return;
    }
    // appState set on loginWithRedirect call below
    const { preLoginPathAndHash } = appState;
    const postLoginPathAndHash = pathname + hash;
    if (preLoginPathAndHash && preLoginPathAndHash !== postLoginPathAndHash) {
      navigate(preLoginPathAndHash);
    }
  };

  return (
    <Auth0Provider
      // URL of the auth0 server
      domain={VITE_AUTH0_DOMAIN}
      // This app's auth0 id
      clientId={VITE_AUTH0_CLIENT_ID}
      // Which app our auth token token is valid for, ensures our token isn't opaque
      authorizationParams={{
        audience: `https://${VITE_AUTH0_DOMAIN}/api/v2/`,
        redirect_uri: window.location.origin,
      }}
      useRefreshTokens
      useRefreshTokensFallback
      // Redirects the user to their original url path after login redirect
      onRedirectCallback={onRedirectCallback}
    >
      <LoginRedirect>{children}</LoginRedirect>
    </Auth0Provider>
  );
}

function LoginRedirect({ children }: { children: JSX.Element }) {
  const { loginWithRedirect, isAuthenticated, isLoading, error } = useAuth0();
  const { pathname, hash } = useLocation();

  // Redirect on app load
  useEffect(() => {
    // Need to guard the loading state because briefly after redirect isAuthenticated is
    // still false while isLoading is true
    if (!isAuthenticated && !isLoading) {
      void loginWithRedirect({
        appState: { preLoginPathAndHash: pathname + hash },
      });
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, pathname, hash]);

  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  if (!isAuthenticated || isLoading) {
    return <LoadingSpinner isFullScreen />;
  }

  if (error) {
    return (
      <ErrorAlert
        isFullScreen
        title="Unable to authenticate"
        icon={<FaExclamationTriangle />}
        message="There was a problem communicating with the authentication server. Please contact us. In the meantime, try refreshing the page."
      />
    );
  }

  return children;
}
